import classNames from 'classnames';
import React, { useEffect, useRef } from 'react';
import { shallowEqual } from 'react-redux';
import { playButtonClickSound } from '@wg/web2clientapi/sound';

import settings from '~/settings';
import { isWCFRequired } from '~/constants';
import dwhExport, { DWH_EVENTS } from '~/dwhExport';
import { t } from '~/helpers/localization';
import useScrollPosition from '~/hooks/useScrollPosition';
import { RefKeys, refManager } from '~/managers/refManager';
import { useAppDispatch, useAppSelector } from '~/store';
import { currentClanSelector } from '~/store/selectors/clanSelectors';
import { fameRatingsSelector, fameSeasonNamesSelector } from '~/store/selectors/hallOfFameSelectors';
import { actionsAutocomplete } from '~/Actions/ActionAutocomplete';
import { toggleCurrentSeason } from '~/Actions/ActionClanRating';
import { actionsSearch } from '~/Actions/ActionSearch';

import ButtonScrollToBottom from '~/Components/ViewClanBattles/ClansBattlesHallFame/ButtonScrollToBottom';
import ButtonScrollToTop from '~/Components/ViewClanBattles/ClansBattlesHallFame/ButtonScrollToTop';
import TopWinners from '~/Components/ViewClanBattles/ClansBattlesHallFame/TopWinners/TopWinners';
import { Title } from '~/UIKit';

import styles from './ClansBattlesHallFame.scss';

import type { Winner } from '~/Reducers/ReducerHallFame';

const ClansBattlesHallFame: React.FC = () => {
  const { winnersList, seasonName } = useAppSelector(fameSeasonNamesSelector, shallowEqual);
  const curentClan = useAppSelector(currentClanSelector, shallowEqual);
  const ratings = useAppSelector(fameRatingsSelector, shallowEqual);
  const dispatch = useAppDispatch();

  const { tag, name } = curentClan || {};

  const ref = useRef<HTMLDivElement | null>(null);
  const app = refManager.getRef(RefKeys.APP);

  const { realmsMap } = settings;
  const seasonsWinners: Array<Winner[]> = Object.values(winnersList).reverse();

  useEffect(() => {
    dwhExport.push(DWH_EVENTS.CLAN_BATTLES.OPEN_HALL_OF_FAME);
  }, []);

  const { isScrolledToBottom: isRefScrolledToBottom, isScrolledToTop: isRefScrolledToTop } = useScrollPosition({
    ref: ref,
    threshold: 10,
    debounceTimeout: 0,
  });
  const { isScrolledToBottom: isAppScrolledToBottom, isScrolledToTop: isAppScrolledToTop } = useScrollPosition({
    ref: app,
    threshold: 10,
    debounceTimeout: 0,
  });

  useEffect(() => {
    if (!isWCFRequired) {
      return;
    }

    const appContainer = app?.current;
    const refContainer = ref.current;

    if (!appContainer || !refContainer) {
      return;
    }

    const handleScroll = (e: WheelEvent) => {
      e.preventDefault();
      e.stopPropagation();

      if (isRefScrolledToBottom) {
        if (e.deltaY > 0) {
          appContainer?.scrollTo({ top: appContainer.scrollHeight, behavior: 'smooth' });
          return;
        }

        if (!isAppScrolledToTop) {
          appContainer?.scrollTo({ top: 0, behavior: 'smooth' });
          return;
        }
      }

      refContainer.scrollBy({
        top: (e.deltaY > 0 ? 1 : -1) * refContainer.offsetHeight,
        behavior: 'smooth',
      });
    };

    appContainer.addEventListener('wheel', handleScroll, { passive: false });
    refContainer.addEventListener('wheel', handleScroll, { passive: false });

    return () => {
      appContainer.removeEventListener('wheel', handleScroll);
      refContainer.removeEventListener('wheel', handleScroll);
    };
  }, [isRefScrolledToBottom, isAppScrolledToBottom, isRefScrolledToTop, isAppScrolledToTop, app]);

  const handleScrollDown = () => {
    void playButtonClickSound();

    ref.current?.scrollTo({ top: ref.current.offsetHeight, behavior: 'smooth' });
  };

  const handleScrollTop = () => {
    void playButtonClickSound();

    ref.current?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const onSeasonChange = (season: number) => {
    dispatch(actionsSearch.changeTerm(''));
    dispatch(toggleCurrentSeason(season));
    dispatch(actionsAutocomplete.clearAutocompleteState());
    dispatch(actionsSearch.clearSearchState());
  };

  return (
    <>
      <video className={styles.video} autoPlay muted loop id="myVideo">
        <source src="https://wows-media-content.gcdn.co/wsclans/3009/sparks.webm" type="video/mp4" />
        <source src="https://wows-media-content.gcdn.co/wsclans/3009/sparks.mp4" type="video/mp4" />
      </video>
      <Title className={styles.title}>{t('Поздравляем лидеров сезона в своих регионах!')}</Title>
      <div className={styles.wrapper}>
        <div
          className={classNames(styles.seasonList, isWCFRequired && isAppScrolledToBottom && styles.noScroll)}
          ref={ref}
        >
          {seasonsWinners.map((season) => (
            <TopWinners
              className={styles.season}
              onSeasonChange={onSeasonChange}
              ownClansName={name}
              ownClansTag={tag}
              ratings={ratings[season[0]?.season_id]}
              realmsMap={realmsMap}
              season={season}
              seasonName={seasonName}
              key={season[0]?.season_id}
            />
          ))}
        </div>
        <ButtonScrollToBottom onClick={handleScrollDown} isHideIconScroll={!isRefScrolledToTop} />
      </div>
      <ButtonScrollToTop onClick={handleScrollTop} isShowScrollToTop={!isRefScrolledToTop} />
    </>
  );
};

export default ClansBattlesHallFame;
